/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { partyScreeningRows, PARTY_STATUS_LABEL } from "./shared/party-screening.js";
import { reconcileCaseState } from "./shared/case-state.js";
import { caseOwnershipThreshold } from "./shared/ownership-threshold.js";
import { reviewPeriodShown } from "./shared/review-period.js";
import { cleanModelText } from "./shared/case-note-text.js";
import { tierInForce, ASSESSMENT_PENDING } from './shared/tier-in-force.js';

// Ownership strings arrive as "92.20%", "~94.79-94.86% effective interest (…)", "50/50".
// parseFloat on a leading "~" is NaN, which silently EXCLUDED the principal UBO from the
// source-of-wealth section while a cleanly formatted corporate holder stayed in
// (Belmoney: Bruno Pedras dropped, XE Investments SARL shown, 25/07/26). Parse the first
// number anywhere in the string instead.
const ownershipPct = (v) => parseFloat(String(v || '').match(/[\d.]+/)?.[0] || '0');
// A PERSONAL source-of-wealth profile applies to natural persons only; a corporate
// shareholder has no personal wealth and its own owners appear as individual UBOs.
const _entitySuffix = /\b(s\.?a\.?r\.?l|s\.?a\.?|s\.?p\.?a|ltd|limited|llc|inc|plc|gmbh|ag|bv|nv|oy|ab|as|aps|kft|sp\s?z\s?o\s?o|pte|sdn|bhd|trust|foundation|stiftung|holdings?|investments?|partners|capital|group|company|corp(oration)?)\b\.?$/i;
// v1_830: at or above the case's ownership threshold (25 when the firm sets none).
const isPersonalSowUbo = (u, threshold = 25) => {
  if (!u || !u.name) return false;
  if (u.corporateUBO === true || u.corporateUBO === 'true') return false;
  if (_entitySuffix.test(String(u.name).trim())) return false;
  return ownershipPct(u.ownership) >= threshold;
};


export default function JaybeeReview() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [review, setReview] = useState(null);
  const [session, setSession] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [decision, setDecision] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [decidedBy, setDecidedBy] = useState("");
  const [verifications, setVerifications] = useState([]);
  const [walletScreenings, setWalletScreenings] = useState([]);
  // v1_827: the firm's live case flags (null until read; false when the read failed).
  const [caseFlags, setCaseFlags] = useState(null);
  // v1_974: the firm whose case it is (read with the case flags). The firm is
  // the obliged entity and decision maker; CompliantID is only the software.
  const [firmName, setFirmName] = useState(null);
  const firmObject = firmName || "the firm";
  const firmSubject = firmName || "The firm";
  const [sowSessions, setSowSessions] = useState([]);
  const [sowDocs, setSowDocs] = useState({});
  const [expandedSow, setExpandedSow] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) { setError("Invalid review link."); setLoading(false); return; }
    loadReview(token);
  }, []);

  const loadReview = async (token) => {
    try {
      fetch('/api/jaybee-decision', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, action: 'case_features' }) })
        .then(r => r.ok ? r.json() : null).then(j => { setCaseFlags(j && j.features ? j.features : false); setFirmName(j && j.firmName ? j.firmName : null); }).catch(() => setCaseFlags(false));
      const { data: reviews, error: re } = await supabase.from("jaybee_reviews").select("*").eq("token", token).single();
      if (re || !reviews) { setError("Review not found or link has expired."); setLoading(false); return; }
      if (reviews.status === "superseded") {
        setError("This review link has been superseded. The firm has sent you an updated link. Please use the most recent email.");
        setLoading(false);
        return;
      }
      if (reviews.status !== "pending") {
        setReview(reviews);
        setSubmitted(true);
        setLoading(false);
        return;
      }
      if (new Date(reviews.expires_at) < new Date()) { setError("This review link has expired. Please contact the firm."); setLoading(false); return; }
      setReview(reviews);

      const { data: sess } = await supabase.from("onboarding_sessions").select("*").eq("id", reviews.session_id).single();
      setSession(sess);

      const { data: docs } = await supabase.from("session_documents").select("*").eq("session_id", reviews.session_id).order("created_at");
      setDocuments(docs || []);
      const { data: verifs } = await supabase.from("sumsub_verifications").select("*").eq("session_id", reviews.session_id);
      setVerifications(verifs || []);
      const { data: wallets } = await supabase.from("wallet_screenings").select("*").eq("session_id", reviews.session_id);
      setWalletScreenings(wallets || []);
      // Build AML checklist from session data
      setSowSessions(prev => prev); // trigger after session loaded

      // Fetch SOW sessions for UBOs
      const { data: sowData } = await supabase.from("onboarding_sessions")
        .select("*")
        .in("status", ["sow_complete", "sow_interview"])
        .order("created_at");
      // Filter to those linked to this parent session
      const linkedSow = (sowData || []).filter(s => 
        s.collected_data?.parentSessionId === reviews.session_id ||
        (session?.contact_email && s.contact_email?.toLowerCase() === session.contact_email?.toLowerCase())
      );
      setSowSessions(linkedSow);
      // Fetch SOW documents for each
      for (const sow of linkedSow) {
        const { data: sd } = await supabase.from("session_documents").select("*").eq("session_id", sow.id).order("created_at");
        setSowDocs(prev => ({ ...prev, [sow.id]: sd || [] }));
      }

    } catch(e) {
      setError("Unable to load review: " + e.message);
    }
    setLoading(false);
  };

  const submitDecision = async (dec) => {
    if (!decidedBy.trim()) { alert("Please enter your name before submitting."); return; }
    if ((dec === "info" || dec === "decline") && !notes.trim()) { alert("Please add notes explaining your decision."); return; }
    setSubmitting(true);
    try {
      const res = await fetch("/api/jaybee-decision", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: review.token, decision: dec, notes, decidedBy })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setDecision(dec);
      setSubmitted(true);
    } catch(e) {
      alert("Error: " + e.message);
    }
    setSubmitting(false);
  };

  const getSignedUrl = async (filePath) => {
    const { data } = await supabase.storage.from("onboarding-documents").createSignedUrl(filePath, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  };

  const S = {
    page: { minHeight: "100vh", background: "#f8fafc", fontFamily: "Georgia, serif", color: "#111827" },
    header: { background: "#1e2d5a", padding: "20px 40px", display: "flex", justifyContent: "space-between", alignItems: "center" },
    container: { maxWidth: 900, margin: "0 auto", padding: "32px 24px" },
    section: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "24px 28px", marginBottom: 20 },
    sectionTitle: { fontSize: 11, fontWeight: 700, color: "#1e2d5a", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 16, paddingBottom: 8, borderBottom: "2px solid #1e2d5a" },
    kv: { display: "flex", gap: 8, marginBottom: 8 },
    kvLabel: { fontSize: 12, color: "#6b7280", minWidth: 140, flexShrink: 0, fontFamily: "Arial, sans-serif" },
    kvVal: { fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", lineHeight: 1.5 },
    para: { fontSize: 13, color: "#374151", lineHeight: 1.8, fontFamily: "Arial, sans-serif", marginBottom: 8 },
    badge: (color) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: color + "20", border: "1px solid " + color + "40", color }),
    docRow: { display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderBottom: "1px solid #f3f4f6" },
    btn: (bg, color) => ({ padding: "12px 28px", background: bg, color, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "Arial, sans-serif" }),
  };

  if (loading) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        <div style={{ color: "#6b7280" }}>Loading review file...</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 400, fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <div style={{ color: "#dc2626", marginBottom: 8, fontWeight: 600 }}>{error}</div>
        <div style={{ color: "#6b7280", fontSize: 13 }}>Please contact the compliance team at compliance@compliantid.com</div>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 500, fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{review?.status === "approved" || decision === "approve" ? "✅" : review?.status === "declined" || decision === "decline" ? "❌" : "📋"}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#1e2d5a", marginBottom: 8 }}>
          {decision === "approve" || review?.status === "approved" ? "Approval Recorded" : decision === "decline" || review?.status === "declined" ? "Decline Recorded" : "Information Requested"}
        </div>
        <div style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.7 }}>
          {decision === "approve" ? `Thank you. Your approval has been recorded and ${firmObject} has been notified. They will proceed with final client onboarding.` :
           decision === "decline" ? `Thank you. Your decision has been recorded and ${firmObject} has been notified.` :
           decision === "info" ? `Thank you. Your request has been sent to ${firmObject}. They will be in touch with the additional information you have requested.` :
           "This review has already been completed."}
        </div>
        {review?.decision_notes && <div style={{ marginTop: 16, padding: "12px 16px", background: "#f9fafb", borderRadius: 8, fontSize: 13, color: "#374151", textAlign: "left" }}><strong>Your notes:</strong> {review.decision_notes}</div>}
      </div>
    </div>
  );

  const report = session?.report || {};
  // v1_747: ONE reconciled case state. The Jaybee review reads screening, the
  // verification party set, SoW/SoF acceptance and the decision from here. A
  // review must never print a verdict it did not read.
  const cs = reconcileCaseState({
    report,
    collected_data: session?.collected_data,
    origin: session?.origin,
    documents,
    documentTracking: [],
    sumsubVerifications: Object.fromEntries((verifications || []).filter(v => v && v.person_name).map(v => [v.person_name, v])),
    aiReview: cleanModelText(session?.ai_review),
    walletScreenings,
    mlroDecision: session?.mlro_decision,
    decisionRecord: session?.collected_data?.decisionRecord,
  // v1_827: the firm's live flags once read, as the dashboard counts them.
  }, caseFlags ? { features: caseFlags } : {});
  const csScreenByName = Object.fromEntries((cs.screening.parties || []).map(p => [String(p.name || '').toLowerCase().trim(), p]));
  const cd = report.companyDetails || {};
  const fc = report.finsaClassification || {};
  const finsaCategory = typeof fc === "object" ? (fc.category || "\u2014") : String(fc || "\u2014");
  const finsaBasis = typeof fc === "object" ? fc.basis : null;
  // Model tier pending (29/10/2026): the tier in force, never the model's riskTier.
  const tierNow = tierInForce(report);
  const riskColor = !tierNow ? "#d97706" : tierNow === "High" ? "#dc2626" : tierNow === "Medium" ? "#d97706" : "#16a34a";

  // v1_747: the recommendation banner READS the recorded decision, never prints a
  // hardcoded "APPROVE". A review must never print a verdict it did not read.
  const jbDecision = String(session?.mlro_decision || report.mlroRecommendation || "").trim();
  const jbNorm = jbDecision.toLowerCase();
  const jbApprove = jbNorm.includes("approve") && !jbNorm.includes("do not") && !jbNorm.includes("decline");
  const jbDecline = jbNorm.includes("decline") || jbNorm.includes("do not approve") || jbNorm.includes("reject");
  const jbColor = jbApprove ? "#16a34a" : jbDecline ? "#dc2626" : "#d97706";
  const jbBg = jbApprove ? "#f0fdf4" : jbDecline ? "#fef2f2" : "#fff7ed";
  const jbText = jbDecision ? jbDecision.toUpperCase() : "PENDING DECISION";

  return (
    <div style={S.page}>
      {/* v1_827: never silently fall back to the case's saved copy of the firm settings. */}
      {caseFlags === false && <div data-case-flags-unread style={{ background: "#fef3c7", color: "#92400e", fontSize: 12, padding: "8px 16px", fontFamily: "Arial, sans-serif" }}>The firm settings could not be read just now, so the checks below follow the copy saved on the case. Reload to try again.</div>}
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={{ fontSize: 11, color: "#84f1ed", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 2 }}>{firmName || "External review"}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>Client Acceptance \u2014 External Review</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#84f1ed" }}>Confidential \u2014 Jaybee Compliance only</div>
          <div style={{ fontSize: 11, color: "#84f1ed" }}>Expires: {review?.expires_at ? new Date(review.expires_at).toLocaleDateString("en-GB") : "\u2014"}</div>
        </div>
      </div>

      <div style={S.container}>
        {/* Recommendation banner */}
        <div style={{ background: jbBg, border: "2px solid " + jbColor, borderRadius: 8, padding: "16px 24px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, color: jbColor, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif" }}>Internal MLRO Recommendation</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: jbColor }}>{jbText}</div>
          </div>
          <div style={{ textAlign: "right", fontFamily: "Arial, sans-serif" }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Reviewed by</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>Compliance Manager, Compliance Manager</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{session?.approved_at ? new Date(session.approved_at).toLocaleDateString("en-GB") : "\u2014"}</div>
          </div>
        </div>

        {/* MLRO conditions */}
        {session?.mlro_notes && (
          <div style={{ ...S.section, borderLeft: "4px solid #1e2d5a" }}>
            <div style={S.sectionTitle}>MLRO Conditions & Notes</div>
            <div style={S.para}>{session.mlro_notes}</div>
          </div>
        )}

        {/* Website Pre-Screening */}
        {(report.websiteFindings?.url && report.websiteFindings?.summary && report.websiteFindings.summary !== "...") && (
          ((report.websiteFindings.redFlags || []).filter(f => f && f !== "...").length > 0 ||
           (report.websiteFindings.contradictions || []).filter(c => c && c !== "...").length > 0) && (
          <div style={{ ...S.section, borderLeft: "4px solid #dc2626" }}>
            <div style={S.sectionTitle}>Website Pre-Screening</div>
            <div style={{ ...S.para, marginBottom:10 }}>{report.websiteFindings.summary}</div>
            {(report.websiteFindings.redFlags || []).filter(f => f && f !== "...").map((f, i) => (
              <div key={i} style={{ fontSize:12, color:"#dc2626", fontFamily:"Arial, sans-serif", lineHeight:1.6, marginBottom:4 }}>🚩 {f}</div>
            ))}
            {(report.websiteFindings.contradictions || []).filter(c => c && c !== "...").map((c, i) => (
              <div key={i} style={{ fontSize:12, color:"#d97706", fontFamily:"Arial, sans-serif", lineHeight:1.6, marginBottom:4 }}>⚠ {c}</div>
            ))}
            {report.websiteFindings.mlroNote && (
              <div style={{ marginTop:10, padding:"10px 14px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:6 }}>
                <div style={{ fontSize:10, color:"#166534", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>✓ MLRO Mitigation Note</div>
                <div style={{ fontSize:12, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{report.websiteFindings.mlroNote}</div>
              </div>
            )}
            {!report.websiteFindings.mlroNote && (
              <div style={{ marginTop:10, padding:"8px 12px", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:6, fontSize:12, color:"#92400e", fontFamily:"Arial, sans-serif" }}>
                ⚠ No MLRO mitigation note recorded for this website discrepancy.
              </div>
            )}
          </div>
          )
        )}

        {/* Six Questions + Procedural Requirements */}
        {session && (() => {
          const r = report;
          const sumsubOk = verifications.some(v => v.status === "approved");
          const allDocsOk = documents.length > 0 && documents.every(d => d.assessment !== "Unsatisfactory");

          const sixQuestions = [
            {
              q: "Is the company legitimate?",
              detail: r.companyDetails?.legalName ? `${r.companyDetails.legalName} · ${r.companyDetails.jurisdiction || ''} · Reg: ${r.companyDetails.registrationNumber || 'see file'}` : "Not confirmed",
              ok: !!(r.companyDetails?.legalName && r.companyDetails?.registrationNumber)
            },
            {
              q: "Are they regulated?",
              detail: r.regulatoryStatus?.regulated ? `${r.regulatoryStatus.regulator} \u2014 ${r.regulatoryStatus.licenceNumber || 'licence on file'}` : r.regulatoryStatus?.licenceType ? `Unregulated \u2014 ${r.regulatoryStatus.licenceType}. ${r.regulatoryStatus.assessment || ''}` : "Unregulated \u2014 see MLRO rationale",
              ok: !!(r.regulatoryStatus?.regulated || r.regulatoryStatus?.assessment)
            },
            {
              q: "Who is the UBO?",
              detail: (r.ubos || []).map(u => `${u.name} ${u.ownership}% (${u.nationality}, ${u.countryOfResidence || ''})`).join('; ') || "Not identified",
              ok: (r.ubos || []).length > 0
            },
            {
              q: "Who runs it?",
              // Carry the role annotation through — see the note in DirectorReview.jsx. A
              // director marked "(publicly reported - unconfirmed by client)" must not read
              // as an established officer.
              detail: (r.directors || []).map(d => {
                if (typeof d !== "object" || !d) return String(d || '');
                const role = String(d.role || '').trim();
                return (role && !/^directors?$/i.test(role)) ? `${d.name} (${role})` : d.name;
              }).filter(Boolean).join(', ') + ((r.authorisedSignatories || []).length > 0 ? ` · Signatories: ${r.authorisedSignatories.map(s => s.name).join(', ')}` : ''),
              ok: (r.directors || []).length > 0
            },
            {
              q: "How does the company make its money?",
              // The business model and the source of funds are DIFFERENT questions. This
              // one was gated on sofAssessment, so a file documenting the business in full
              // still reported "Not assessed" whenever the funding account was outstanding
              // \u2014 Belmoney, an NBB-authorised payment institution with its whole revenue
              // model on file, read as unassessed (25/07/26). Answer it from the business
              // description; funding is the next question down.
              detail: r.companyDetails?.businessDescription
                ? String(r.companyDetails.businessDescription).slice(0, 120)
                : `${cs.sof.displayScore || 'Not assessed'} \u2014 ${(cs.sof.narrative || 'see report').slice(0,100)}`,
              ok: !!(r.companyDetails?.businessDescription || (cs.sof.displayScore && cs.sof.displayScore !== 'Unsatisfactory'))
            },
            {
              q: "Where are funds coming from?",
              detail: r.sofAssessment?.namedAccount ? `${r.sofAssessment.namedAccount}${r.sofAssessment.fundingRoute ? ' · ' + r.sofAssessment.fundingRoute.slice(0,80) : ''}` : "Not confirmed",
              ok: !!(r.sofAssessment?.namedAccount)
            }
          ];

          const hasW = walletScreenings.length > 0;
          const tr = r.travelRule;
          // Natural persons only — a corporate shareholder has no personal source of wealth.
          const ubos25 = (r.ubos || []).filter(u => isPersonalSowUbo(u, caseOwnershipThreshold(r)));

          const procedural = [
            {
              q: "Identity verified (SumSub)",
              detail: cs.partyCount === 0 ? "Not yet completed" : cs.verifiedCount === cs.partyCount ? `${cs.verifiedCount} of ${cs.partyCount} individuals verified` : `${cs.verifiedCount} of ${cs.partyCount} verified \u2014 some pending`,
              ok: sumsubOk
            },
            {
              q: "Wallet screening (Chainalysis)",
              detail: hasW ? `${walletScreenings.length} wallet(s) screened \u2014 ${walletScreenings.map(w => `${w.risk_level || 'Unknown'}`).join(', ')}` : "No crypto wallets declared",
              ok: !hasW || walletScreenings.length > 0
            },
            {
              q: "Travel Rule (FATF/Virtual Assets)",
              detail: !tr || walletScreenings.length === 0 ? "No crypto wallets \u2014 not applicable" : tr.overallStatus === "compliant" ? "All wallets verified" : `${(tr.wallets || []).filter(w => w.travelRuleStatus !== "compliant").length} wallet(s) pending`,
              ok: !tr || walletScreenings.length === 0 || tr.overallStatus === "compliant"
            },
            {
              q: "UBO Source of Wealth interview",
              detail: ubos25.length === 0 ? `No UBOs at or above the ${caseOwnershipThreshold(r)}% threshold` : ubos25.every(u => u.sowAssessment || u.sowInterviewComplete) ? `${ubos25.map(u => u.name).join(', ')}: interviewed and assessed` : `${ubos25.filter(u => !u.sowAssessment && !u.sowInterviewComplete).map(u => u.name).join(', ')}: pending`,
              ok: ubos25.length === 0 || ubos25.every(u => u.sowAssessment || u.sowInterviewComplete)
            }
          ];

          const allOk = sixQuestions.every(c => c.ok) && procedural.every(p => p.ok);
          const totalDone = [...sixQuestions, ...procedural].filter(c => c.ok).length;
          const total = sixQuestions.length + procedural.length;

          return (
            <div style={{ ...S.section, marginBottom: 20 }}>
              <div style={S.sectionTitle}>What the MLRO has established</div>
              <div style={{ ...S.para, marginBottom: 14 }}>
                The following summarises how the MLRO has addressed the six key questions required under AML/CTF regulations, together with the four mandatory procedural requirements. All items were completed and documented prior to Director approval and this submission.
              </div>
              <div style={{ background: allOk ? "#f0fdf4" : "#fff7ed", border: `1px solid ${allOk ? "#86efac" : "#fed7aa"}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: allOk ? "#15803d" : "#d97706", fontFamily: "Arial, sans-serif" }}>
                  {allOk ? `✓ All ${total} items addressed \u2014 file is complete` : `⚠ ${totalDone} of ${total} items addressed \u2014 see details below`}
                </div>
              </div>

              {/* Six Questions */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#1e2d5a", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8, fontFamily: "Arial, sans-serif" }}>Six Key Questions</div>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                  <thead>
                    <tr style={{ background: "#1e2d5a" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 220 }}>Question</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 50 }}>Status</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Evidence / MLRO Finding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sixQuestions.map((c, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 12px", fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>{c.q}</td>
                        <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 16 }}>{c.ok ? "✅" : "⚠️"}</td>
                        <td style={{ padding: "8px 12px", fontSize: 12, color: c.ok ? "#374151" : "#d97706", fontFamily: "Arial, sans-serif" }}>{c.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Procedural */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#1e2d5a", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8, fontFamily: "Arial, sans-serif" }}>Procedural Requirements</div>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                  <thead>
                    <tr style={{ background: "#1e2d5a" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 220 }}>Requirement</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 50 }}>Status</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {procedural.map((c, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 12px", fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>{c.q}</td>
                        <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 16 }}>{c.ok ? "✅" : "⚠️"}</td>
                        <td style={{ padding: "8px 12px", fontSize: 12, color: c.ok ? "#374151" : "#d97706", fontFamily: "Arial, sans-serif" }}>{c.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Object & Purpose of the Business Relationship — Art. 6 AMLA */}
        {(() => {
          const oap = report.objectAndPurpose || {};
          const has = !!(oap.object || oap.purpose);
          return (
            <div style={S.section}>
              <div style={S.sectionTitle}>Object &amp; Purpose of the Business Relationship</div>
              <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 10 }}>Art. 6 AMLA: nature and purpose of the requested business relationship</div>
              {has ? (
                <>
                  {oap.object && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 11, color: "#1e2d5a", fontWeight: 700, fontFamily: "Arial, sans-serif", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>Object — requested service</div><div style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", lineHeight: 1.6 }}>{String(oap.object)}</div></div>}
                  {oap.purpose && <div><div style={{ fontSize: 11, color: "#1e2d5a", fontWeight: 700, fontFamily: "Arial, sans-serif", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>Purpose — why the client seeks the service</div><div style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", lineHeight: 1.6 }}>{String(oap.purpose)}</div></div>}
                  {(oap.by || oap.at) && <div style={{ fontSize: 10, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginTop: 8 }}>Documented by {String(oap.by || "MLRO")}{oap.at ? " · " + new Date(oap.at).toLocaleDateString("en-GB") : ""}</div>}
                </>
              ) : (
                <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 12px", fontFamily: "Arial, sans-serif" }}>
                  Not yet documented in structured form for this file. The underlying information is captured in the business profile and anticipated activity sections.
                </div>
              )}
            </div>
          );
        })()}

        {/* Entity Sanctions Screening — OpenSanctions (company + additional entities) */}
        {(() => {
          const normaliseScreening = (raw) => {
            if (!raw) return null;
            if (raw.status && !raw.entityChecked) return raw;
            if (raw.keyRequired) return { status: "no_key" };
            if (raw.error) return { status: "error" };
            const dismissed = raw.dismissed || {};
            const allMatches = [...(raw.hits || []), ...(raw.cautionMatches || [])];
            const allDismissed = allMatches.length > 0 && allMatches.every(h => dismissed[h.name]);
            if (allDismissed) return { status: "clear_with_notes", data: raw };
            if (raw.sanctioned) return { status: "hit", data: raw };
            if (raw.hasPartialMatches) return { status: "caution", data: raw };
            return { status: "clear", data: raw };
          };
          const es = normaliseScreening(session?.collected_data?.entitySanctions);
          const extras = (session?.collected_data?.additionalSanctionsScreenings || []).map(a => {
            const n = (a.data && (a.data.entityChecked || a.data.hits || a.data.sanctioned !== undefined)) ? normaliseScreening(a.data) : null;
            return { name: a.name, checkedAt: a.checkedAt, status: n?.status || a.status, data: n?.data || a.data || {} };
          });
          const st = es?.status;
          const d = es?.data || {};
          const head = st === "clear" ? ["#15803d", "✓ Clear"]
            : st === "clear_with_notes" ? ["#15803d", "✓ Clear (false positives noted)"]
            : st === "hit" ? ["#dc2626", "⚠ Sanctions hit"]
            : st === "caution" ? ["#d97706", "◈ Partial match — under review"]
            : ["#9ca3af", st === "running" ? "Screening…" : "Not yet run"];
          const badge = (c) => ({ display: "inline-block", padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#fff", background: c, fontFamily: "Arial, sans-serif" });
          const dism = d.dismissed || {};
          const matches = [...(d.hits || []).map(h => ({ ...h, level: "hit" })), ...(d.cautionMatches || []).map(h => ({ ...h, level: "caution" }))];
          return (
            <div style={S.section}>
              <div style={S.sectionTitle}>Entity Sanctions Screening (OpenSanctions)</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>{String(d.entityChecked || session?.company_name || "Company")}</div>
                <span style={badge(head[0])}>{head[1]}</span>
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", marginBottom: 10 }}>
                {d.listsSearched ? String(d.listsSearched) : "OFAC, EU, UN, UK HMT, Swiss SECO + 200 others via OpenSanctions"}{d.checkedAt ? " · Screened " + new Date(d.checkedAt).toLocaleString("en-GB") : ""}
              </div>
              {matches.map((h, i) => {
                const dis = dism[h.name];
                return (
                  <div key={i} style={{ background: dis ? "#f0fdf4" : h.level === "hit" ? "#fff1f2" : "#fff7ed", border: "1px solid " + (dis ? "#bbf7d0" : h.level === "hit" ? "#fecaca" : "#fed7aa"), borderRadius: 6, padding: "8px 12px", marginBottom: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: dis ? "#6b7280" : h.level === "hit" ? "#991b1b" : "#92400e", textDecoration: dis ? "line-through" : "none", fontFamily: "Arial, sans-serif" }}>{String(h.name)} — match {h.score}%</div>
                    <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif" }}>Lists: {(h.datasets || []).join(", ")}</div>
                    {dis && <div style={{ fontSize: 11, color: "#15803d", fontFamily: "Arial, sans-serif", marginTop: 4, paddingLeft: 8, borderLeft: "2px solid #bbf7d0" }}>✓ False positive — {String(dis.dismissedBy || "")}{dis.dismissedAt ? ", " + new Date(dis.dismissedAt).toLocaleDateString("en-GB") : ""}: {String(dis.reason || "")}</div>}
                  </div>
                );
              })}
              {extras.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", fontWeight: 700, marginBottom: 6 }}>Additional Entities Screened (counterparties / related parties)</div>
                  {extras.map((a, ai) => {
                    const aPill = a.status === "hit" ? ["#dc2626", "⚠ Sanctions hit"] : a.status === "caution" ? ["#d97706", "◈ Partial match"]
                      : a.status === "clear_with_notes" ? ["#15803d", "✓ Clear (notes)"] : a.status === "clear" ? ["#15803d", "✓ Clear"] : ["#9ca3af", a.status || "—"];
                    const aDism = a.data?.dismissed || {};
                    return (
                      <div key={ai} style={{ marginBottom: 5 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 10px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6 }}>
                          <div style={{ minWidth: 0 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#111827", fontFamily: "Arial, sans-serif" }}>{String(a.name)}</span>
                            <span style={{ fontSize: 10, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginLeft: 8 }}>{a.checkedAt ? new Date(a.checkedAt).toLocaleDateString("en-GB") : ""}</span>
                          </div>
                          <span style={badge(aPill[0])}>{aPill[1]}</span>
                        </div>
                        {Object.entries(aDism).map(([hitName, dd], di) => (
                          <div key={di} style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", padding: "4px 10px", marginTop: 3, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6 }}>
                            <span style={{ textDecoration: "line-through" }}>{String(hitName)}</span>
                            <span style={{ color: "#15803d" }}> — false positive confirmed by {String(dd.dismissedBy || "")}{dd.dismissedAt ? ", " + new Date(dd.dismissedAt).toLocaleDateString("en-GB") : ""}: {String(dd.reason || "")}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Per-director/UBO OpenSanctions results (v1_645). Each individual as its own
                  row, an independent OpenSanctions record distinct from identity verification. */}
              {(() => {
                const rows = partyScreeningRows(session?.report || session?.collected_data?.documentsBoard || {}, session?.collected_data?.partyScreenings, session?.collected_data?.screeningResolutions);
                if (!rows.length) return null;
                const col = (s) => s === "hit" ? "#dc2626" : s === "caution" ? "#d97706" : s === "clear" ? "#15803d" : "#9ca3af";
                return (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", fontWeight: 700, marginBottom: 6 }}>Individuals (OpenSanctions Screening, independent of identity verification)</div>
                    {rows.map((pr, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 10px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 5 }}>
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#111827", fontFamily: "Arial, sans-serif" }}>{String(pr.name)}</span>
                          <span style={{ fontSize: 10, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginLeft: 8 }}>{pr.role}{pr.checkedAt ? " · " + new Date(pr.checkedAt).toLocaleDateString("en-GB") : ""}</span>
                        </div>
                        <span style={badge(col(pr.status))}>{PARTY_STATUS_LABEL[pr.status] || pr.status}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* Client Profile */}
        <div style={S.section}>
          <div style={S.sectionTitle}>1. Client Profile</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
            {[
              ["Legal Name", cd.legalName || session?.company_name],
              ["Jurisdiction", cd.jurisdiction || report.jurisdiction],
              ["Registration No.", cd.registrationNumber || report.registrationNumber],
              ["Incorporated", cd.dateOfIncorporation],
              ["Registered Address", cd.registeredAddress || report.registeredAddress],
              ["Business", cd.businessDescription || report.businessDescription],
            ].filter(([,v]) => v).map(([label, val]) => (
              <div key={label} style={S.kv}>
                <span style={S.kvLabel}>{label}</span>
                <span style={S.kvVal}>{val}</span>
              </div>
            ))}
          </div>
          {(cd.groupStructure || report.groupStructure) && <div style={{ marginTop: 12, ...S.para }}><strong>Group structure:</strong> {cd.groupStructure || report.groupStructure}</div>}
        </div>

        {/* Risk & FinSA */}
        <div style={S.section}>
          <div style={S.sectionTitle}>2. Risk Assessment & FinSA Classification</div>
          <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ padding: "12px 20px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, color: "#888888", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Risk Tier</div>
              <span style={S.badge(riskColor)}>{tierNow || ASSESSMENT_PENDING}</span>
            </div>
            <div style={{ padding: "12px 20px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, color: "#888888", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>FinSA Classification</div>
              <span style={S.badge("#1e2d5a")}>{finsaCategory}</span>
            </div>
            <div style={{ padding: "12px 20px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, color: "#888888", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Review Period</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "Arial, sans-serif" }}>{reviewPeriodShown(report, { approved: !!(session?.approved_at || session?.mlro_decision === 'Approve') })}</div>
            </div>
          </div>
          {finsaBasis && <div style={S.para}><strong>FinSA basis:</strong> {finsaBasis}</div>}
          {(typeof fc === "object" && fc.assessment) && <div style={S.para}>{fc.assessment}</div>}
          {report.riskRationale && <div style={{ ...S.para, marginTop: 8 }}>{report.riskRationale}</div>}
        </div>

        {/* Beneficial Ownership */}
        <div style={S.section}>
          <div style={S.sectionTitle}>3. Beneficial Ownership & Identity Verification</div>
          {(report.ubos || []).map((ubo, i) => {
            const verifMatch = verifications.find(v => v.person_name?.toLowerCase().includes(ubo.name?.split(" ")[0]?.toLowerCase()));
            return (
              <div key={i} style={{ padding: "14px 16px", background: "#f9fafb", borderRadius: 8, marginBottom: 10, border: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", fontFamily: "Arial, sans-serif" }}>{ubo.name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 2 }}>{ubo.ownership} · {ubo.nationality}{ubo.countryOfResidence ? " · Resident: " + ubo.countryOfResidence : ""}</div>
                  </div>
                  <span style={S.badge(parseFloat(ubo.ownership) < caseOwnershipThreshold(report) ? "#6b7280" : ubo.corporateUBO ? "#7c3aed" : "#1e2d5a")}>{parseFloat(ubo.ownership) < caseOwnershipThreshold(report) ? `Shareholder (below ${caseOwnershipThreshold(report)}%)` : ubo.corporateUBO ? "Corporate UBO" : "Beneficial Owner"}</span>
                </div>
                {/* Identity verification */}
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <div style={{ padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e5e7eb", flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 10, color: "#888888", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>ID Verification (Sumsub)</div>
                    {verifMatch ? (
                      <div>
                        <span style={S.badge(verifMatch.status === "approved" ? "#15803d" : verifMatch.status === "pending" ? "#d97706" : "#dc2626")}>
                          {verifMatch.status === "approved" ? "✓ Verified" : verifMatch.status === "pending" ? "⏳ Pending" : "✗ " + verifMatch.status}
                        </span>
                        {verifMatch.applicant_id && <div style={{ fontSize: 10, color: "#888888", fontFamily: "Arial, sans-serif", marginTop: 4 }}>Ref: {verifMatch.applicant_id}</div>}
                      </div>
                    ) : (
                      <span style={S.badge("#888888")}>Pending</span>
                    )}
                  </div>
                  <div style={{ padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e5e7eb", flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 10, color: "#888888", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>PEP / Sanctions</div>
                    {(() => {
                      // v1_747: READ the reconciled per-party screening result, never
                      // print a hardcoded "No match". A verdict must be one we read.
                      const pr = csScreenByName[String(ubo.name || '').toLowerCase().trim()];
                      const st = pr ? pr.status : 'not_screened';
                      const badge = st === 'clear' ? { c: '#15803d', t: 'No match' }
                        : st === 'hit' ? { c: '#dc2626', t: 'Match — review' }
                        : st === 'caution' ? { c: '#d97706', t: 'Partial — review' }
                        : { c: '#888888', t: 'Not screened' };
                      return <span style={S.badge(badge.c)}>{badge.t}</span>;
                    })()}
                  </div>
                  {ubo.controlBeyondOwnership !== undefined && (
                    <div style={{ padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e5e7eb", flex: 1, minWidth: 160 }}>
                      <div style={{ fontSize: 10, color: "#888888", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Control beyond ownership</div>
                      <span style={S.badge(ubo.controlBeyondOwnership ? "#d97706" : "#15803d")}>{ubo.controlBeyondOwnership ? "Yes" : "No"}</span>
                      {ubo.controlNotes && <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 4 }}>{ubo.controlNotes}</div>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {(report.directors || []).length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: "#888888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8, fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Directors / Authorised Persons</div>
              {report.directors.map((d, i) => {
                const name = typeof d === "object" ? d.name : d;
                const role = typeof d === "object" ? d.role : "Director";
                const verifD = verifications.find(v => v.person_name?.toLowerCase().includes(name?.split(" ")[0]?.toLowerCase()));
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 10px", background: "#f9fafb", borderRadius: 6, marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 500 }}>{name}</span>
                      <span style={{ fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginLeft: 8 }}>{role}</span>
                    </div>
                    {verifD ? <span style={S.badge(verifD.status === "approved" ? "#15803d" : "#d97706")}>{verifD.status === "approved" ? "✓ Verified" : "⏳ Pending"}</span> : <span style={S.badge("#888888")}>Pending</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Source of Funds */}
        {report.sofAssessment && (
          <div style={S.section}>
            <div style={S.sectionTitle}>4. Source of Funds</div>
                        {cs.sof.displayScore && <div style={S.kv}><span style={S.kvLabel}>Assessment</span><span style={S.kvVal}>{cs.sof.displayScore}{cs.sof.accepted && cs.sof.modelScore ? ` (the analysis assessed this ${String(cs.sof.modelScore).split(/[—–-]/)[0].trim()})` : ''}</span></div>}
            {report.sofAssessment.namedAccount && <div style={S.kv}><span style={S.kvLabel}>Named Account</span><span style={S.kvVal}>{(report.sofAssessment.namedAccount)}</span></div>}
{report.sofAssessment.fundingRoute && <div style={S.kv}><span style={S.kvLabel}>Funding Route</span><span style={S.kvVal}>{report.sofAssessment.fundingRoute}</span></div>}
            {report.sofAssessment.expectedVolumes && <div style={S.kv}><span style={S.kvLabel}>Expected Volumes</span><span style={S.kvVal}>{report.sofAssessment.expectedVolumes}</span></div>}
            {/* The schema field is `narrative`; `assessment` (kept for legacy reports) never
                existed on newer ones, so the substantive source-of-funds text was silently
                dropped from the signed review while the dashboard showed it in full. */}
            {report.sofAssessment.narrative && <div style={{ ...S.para, marginTop: 8 }}>{(report.sofAssessment.narrative)}</div>}
            {report.sofAssessment.assessment && <div style={{ ...S.para, marginTop: 8 }}>{report.sofAssessment.assessment}</div>}
            {report.sofAssessment.notes && <div style={{ ...S.para, marginTop: 6, fontStyle: 'italic', color: '#6b7280' }}>{(report.sofAssessment.notes)}</div>}
          </div>
        )}

        {/* Source of Wealth \u2014 unified section 5 */}
        {((report.ubos || []).some(u => isPersonalSowUbo(u, caseOwnershipThreshold(report))) || sowSessions.length > 0 || report.sowAssessment?.narrative) && (
          <div style={S.section}>
            <div style={S.sectionTitle}>5. Source of Wealth: UBOs at or above {caseOwnershipThreshold(report)}%</div>
            {/* This document is signed by a third party, so it must never assert steps that have
                not happened: the previous fixed text claimed every profile was "reviewed and
                signed off by the internal MLRO" even while an interview was still pending
                (Belmoney, 25/07/26). State what is true for the profiles actually shown. */}
            {report.sowAssessment?.narrative && (
              <div style={{ marginBottom: 14 }}>
                {cs.sow.displayScore && <div style={S.kv}><span style={S.kvLabel}>Assessment</span><span style={S.kvVal}>{cs.sow.displayScore}{cs.sow.accepted && cs.sow.modelScore ? ` (the analysis assessed this ${String(cs.sow.modelScore).split(/[—–-]/)[0].trim()})` : ''}</span></div>}
                <div style={{ ...S.para, marginTop: 6 }}>{String(report.sowAssessment.narrative)}</div>
                {report.sowAssessment.notes && <div style={{ ...S.para, marginTop: 6, fontStyle: 'italic', color: '#6b7280' }}>{String(report.sowAssessment.notes)}</div>}
              </div>
            )}
            <div style={{ ...S.para, color: "#6b7280", marginBottom: 16 }}>
              {(() => {
                const shown = (report.ubos || []).filter(u => isPersonalSowUbo(u, caseOwnershipThreshold(report)));
                const complete = shown.filter(u => u.sowNarrative || u.sowAssessment || sowSessions.some(s2 => s2 && s2.report && (s2.contact_email?.toLowerCase() === u.email?.toLowerCase() || s2.contact_name?.toLowerCase().includes(u.name?.split(" ")[0]?.toLowerCase()))));
                if (shown.length === 0) return `No natural person UBO at or above the ${caseOwnershipThreshold(report)}% threshold has a personal source of wealth profile on this file.`;
                if (complete.length === shown.length) return 'Source of wealth profiles were collected via a structured compliance interview and reviewed by the internal MLRO.';
                return `Source of wealth profiles are collected via a structured compliance interview. ${complete.length} of ${shown.length} profile${shown.length === 1 ? '' : 's'} ${complete.length === 1 ? 'is' : 'are'} on file; the remainder are pending and this section will be incomplete until they are.`;
              })()}
            </div>
            {(report.ubos || []).filter(u => isPersonalSowUbo(u, caseOwnershipThreshold(report))).map((ubo, i) => {
              const sowSess = sowSessions.find(s => 
                s.contact_email?.toLowerCase() === ubo.email?.toLowerCase() || 
                s.contact_name?.toLowerCase().includes(ubo.name?.split(" ")[0]?.toLowerCase())
              );
              const sowReport = sowSess?.report || {};
              const isExpanded = expandedSow[ubo.name] || false;
              const assessment = ubo.sowAssessment || sowReport.sowAssessment;
              return (
                <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12, overflow: "hidden" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: "#f9fafb", cursor: "pointer" }}
                    onClick={() => setExpandedSow(prev => ({ ...prev, [ubo.name]: !prev[ubo.name] }))}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Arial, sans-serif" }}>{ubo.name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 2 }}>{ubo.ownership} · {ubo.nationality}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {assessment && <span style={S.badge(assessment.includes("Satisfactory") ? "#15803d" : assessment.includes("Basic") || assessment.includes("Clarification") ? "#d97706" : "#dc2626")}>{assessment}</span>}
                      <span style={{ fontSize: 16, color: "#888888" }}>{isExpanded ? "▲" : "▼"}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: "16px 20px" }}>
                      {/* MLRO summary narrative */}
                      {ubo.sowNarrative && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 11, color: "#888888", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 6, fontWeight: 700 }}>MLRO Summary</div>
                          <div style={S.para}>{ubo.sowNarrative}</div>
                          {ubo.sowMlroRationale && <div style={{ ...S.para, fontStyle: "italic", color: "#6b7280", marginTop: 4, paddingLeft: 12, borderLeft: "3px solid #e5e7eb" }}>{ubo.sowMlroRationale}</div>}
                        </div>
                      )}
                      {/* Full interview detail */}
                      {sowSess && (
                        <div>
                          {sowReport.professionalBackground && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#888888", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Professional Background</div>
                              <div style={S.para}>{sowReport.professionalBackground}</div>
                            </div>
                          )}
                          {sowReport.shareholdingOrigin && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#888888", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Shareholding Origin</div>
                              <div style={S.para}>{sowReport.shareholdingOrigin}</div>
                            </div>
                          )}
                          {(sowReport.sowNotes || sowReport.wealthOrigins) && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#888888", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Wealth Origins</div>
                              <div style={S.para}>{sowReport.sowNotes || sowReport.wealthOrigins}</div>
                            </div>
                          )}
                          {sowReport.significantEvents && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#888888", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Significant Events</div>
                              <div style={S.para}>{sowReport.significantEvents}</div>
                            </div>
                          )}
                          {[["Wealth timeframe", sowReport.wealthTimeframe], ["Wealth structure", sowReport.wealthStructure]].filter(([,v]) => v).map(([label, val]) => (
                            <div key={label} style={S.kv}><span style={S.kvLabel}>{label}</span><span style={S.kvVal}>{val}</span></div>
                          ))}
                        </div>
                      )}
                      {/* Evidence */}
                      {(ubo.evidenceProvided || sowReport.evidenceProvided) && (
                        <div style={{ padding: "10px 14px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, margin: "12px 0" }}>
                          <div style={{ fontSize: 11, color: "#0369a1", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Evidence Provided</div>
                          <div style={{ fontSize: 13, color: "#0c4a6e", fontFamily: "Arial, sans-serif", lineHeight: 1.6 }}>{ubo.evidenceProvided || sowReport.evidenceProvided}</div>
                        </div>
                      )}
                      {/* SOW documents */}
                      {sowSess && (sowDocs[sowSess.id] || []).length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 11, color: "#888888", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 8, fontWeight: 700 }}>Supporting Documents</div>
                          {sowDocs[sowSess.id].map((doc, di) => (
                            <div key={di} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "#f9fafb", borderRadius: 6, marginBottom: 6, border: "1px solid #e5e7eb" }}>
                              <span style={{ fontSize: 18 }}>📄</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 500 }}>{doc.file_name}</div>
                                {doc.assessment && <div style={{ fontSize: 11, color: doc.assessment === "Satisfactory" ? "#15803d" : "#d97706", fontFamily: "Arial, sans-serif", marginTop: 2, fontWeight: 600 }}>{doc.assessment}</div>}
                              </div>
                              {(doc.file_path && doc.file_path !== 'pending') ? (
                                <button onClick={async () => {
                                  const { data } = await supabase.storage.from("onboarding-documents").createSignedUrl(doc.file_path, 3600);
                                  if (data?.signedUrl) window.open(data.signedUrl, "_blank");
                                }} style={{ padding: "6px 14px", background: "#1e2d5a", color: "#fff", border: "none", borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>View ↗</button>
                              ) : <span style={{ fontSize: 11, color: "#d1d5db" }}>No file</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {sowSess?.updated_at && <div style={{ marginTop: 10, fontSize: 11, color: "#888888", fontFamily: "Arial, sans-serif" }}>Interview completed: {new Date(sowSess.updated_at).toLocaleDateString("en-GB")}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

                {/* Tax Compliance */}
        <div style={S.section}>
          <div style={S.sectionTitle}>6b. Tax Compliance Declaration</div>
          {report.taxCompliance?.riskIndicatorsPresent ? (
            <div style={{ padding:"10px 14px", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:6 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#d97706", fontFamily:"Arial, sans-serif", marginBottom:4 }}>⚠ Risk Indicators Present</div>
              <div style={{ fontSize:12, color:"#92400e", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{report.taxCompliance.notes}</div>
              {(report.taxCompliance.riskIndicators || []).map((r,i) => <div key={i} style={{ fontSize:11, color:"#92400e", fontFamily:"Arial, sans-serif" }}>• {r}</div>)}
            </div>
          ) : report.taxCompliance?.declared ? (
            <div style={{ padding:"10px 14px", background:"#f0fdf4", border:"1px solid #86efac", borderRadius:6 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#15803d", fontFamily:"Arial, sans-serif", marginBottom:4 }}>✓ Declaration Obtained</div>
              <div style={{ fontSize:12, color:"#166534", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{report.taxCompliance.declarationText || "Client confirmed assets are fully tax compliant in all relevant jurisdictions."}</div>
            </div>
          ) : (
            <div style={{ ...S.para, color:"#888888" }}>Tax compliance declaration not yet recorded.</div>
          )}
          <div style={{ fontSize:11, color:"#888888", fontFamily:"Arial, sans-serif", marginTop:8 }}>Required under applicable AML regulations</div>
        </div>

        {/* Documents */}
        <div style={S.section}>
          <div style={S.sectionTitle}>7. Documents on File ({documents.length})</div>
          {documents.length === 0 ? <div style={{ ...S.para, color: "#888888" }}>No documents uploaded</div> : (
            <div>
              {documents.map((doc, i) => (
                <div key={i} style={{ padding:"12px 14px", borderRadius:6, border:"1px solid #e5e7eb", marginBottom:8, background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:"#111827", fontFamily:"Arial, sans-serif", marginBottom:2 }}>{doc.file_name}</div>
                      {doc.doc_type && <div style={{ fontSize:11, color:"#888888", fontFamily:"Arial, sans-serif", marginBottom: doc.analysis ? 6 : 0 }}>{doc.doc_type}</div>}
                      {(() => {
                        // Parse analysis whether stored as object or JSON string
                        let a = doc.analysis;
                        if (typeof a === "string") { try { a = JSON.parse(a); } catch(e) { a = null; } }
                        if (!a) return null;
                        const findings = a.keyFindings || [];
                        const materialFlags = [...(a.materialFlags||[]), ...(a.redFlags||[])];
                        const notedFlags = a.notedFlags || [];
                        const informationals = a.informationals || [];
                        return (
                          <div style={{ paddingLeft:8, borderLeft:"3px solid #e5e7eb" }}>
                            {findings.length > 0 && findings.map((f, fi) => (
                              <div key={fi} style={{ fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif", lineHeight:1.6, marginBottom:2 }}>\u2014 {f}</div>
                            ))}
                            {/* Material flags \u2014 red, require action or documented mitigation */}
                            {materialFlags.length > 0 && (
                              <div style={{ marginTop:6, padding:"8px 10px", background:"#fff1f2", border:"1px solid #fecaca", borderRadius:4 }}>
                                <div style={{ fontSize:10, color:"#991b1b", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>🔴 Material \u2014 Action Required</div>
                                {materialFlags.map((f, fi) => (
                                  <div key={fi} style={{ fontSize:12, color:"#7f1d1d", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>• {f}</div>
                                ))}
                              </div>
                            )}
                            {/* Noted flags \u2014 amber, standard for client type */}
                            {notedFlags.length > 0 && (
                              <div style={{ marginTop:6, padding:"8px 10px", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:4 }}>
                                <div style={{ fontSize:10, color:"#92400e", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>🟡 Noted \u2014 Standard for Client Type</div>
                                {notedFlags.map((f, fi) => (
                                  <div key={fi} style={{ fontSize:12, color:"#78350f", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>• {f}</div>
                                ))}
                              </div>
                            )}
                            {/* Informationals \u2014 blue, factual context only */}
                            {informationals.length > 0 && (
                              <div style={{ marginTop:6, padding:"8px 10px", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:4 }}>
                                <div style={{ fontSize:10, color:"#1e40af", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>ℹ️ Informational</div>
                                {informationals.map((f, fi) => (
                                  <div key={fi} style={{ fontSize:12, color:"#1e3a8a", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>• {f}</div>
                                ))}
                              </div>
                            )}
                            {/* MLRO mitigation note \u2014 always shown when present, always retained in audit trail */}
                            {doc.mlro_note && (
                              <div style={{ marginTop:6, padding:"8px 10px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:4 }}>
                                <div style={{ fontSize:10, color:"#166534", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:3 }}>✓ MLRO Mitigation Note</div>
                                <div style={{ fontSize:12, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.5 }}>{doc.mlro_note}</div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6, flexShrink:0 }}>
                      <span style={S.badge(doc.assessment === "Satisfactory" ? "#15803d" : doc.assessment === "Requires Clarification" ? "#d97706" : "#6b7280")}>
                        {doc.assessment || "Received"}
                      </span>
                      {(doc.file_path && doc.file_path !== 'pending') ? (
                        <button onClick={() => getSignedUrl(doc.file_path)} style={{ fontSize:11, color:"#1e2d5a", background:"transparent", border:"1px solid #1e2d5a", borderRadius:4, padding:"3px 10px", cursor:"pointer", fontFamily:"Arial, sans-serif" }}>View</button>
                      ) : <span style={{ fontSize:11, color:"#d1d5db" }}>\u2014</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* MLRO Sign-off statement */}
        <div style={S.section}>
          <div style={S.sectionTitle}>8. MLRO Internal Review &amp; Sign-off</div>
          <div style={{ padding:"14px 16px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8 }}>
            <div style={{ fontSize:13, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.7 }}>
              This file has been reviewed by the internal MLRO at {firmObject}. All risk factors identified during the onboarding process have been assessed, mitigated where appropriate, and signed off internally prior to submission for external compliance review.
            </div>
            {session?.mlro_notes && (
              <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid #bbf7d0" }}>
                <div style={{ fontSize:11, color:"#166534", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>MLRO Notes</div>
                <div style={{ fontSize:13, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{session.mlro_notes}</div>
              </div>
            )}
            <div style={{ marginTop:10, fontSize:12, color:"#166534", fontFamily:"Arial, sans-serif" }}>
              Approved by: {session?.mlro_decision ? session.mlro_decision + " \u2014 " : ""}{new Date(session?.approved_at || "").toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" })}
            </div>
          </div>
        </div>

        {/* Risk Factors & Mitigations */}
        {(report.riskFactors?.length > 0) && (
          <div style={S.section}>
            <div style={S.sectionTitle}>9. Risk Assessment &amp; Mitigants</div>
            <div style={{ ...S.para, color:"#6b7280", marginBottom:10 }}>
              The following risk factors were identified during the onboarding process. Each has been assessed by the internal MLRO and mitigating controls identified. This assessment informed the decision to approve this client relationship.
            </div>
            <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, padding:"3px 10px", borderRadius:4, background:"#fff1f2", border:"1px solid #fecaca", color:"#991b1b", fontFamily:"Arial, sans-serif", fontWeight:600 }}>🔴 Material \u2014 requires action or documented mitigation</span>
              <span style={{ fontSize:11, padding:"3px 10px", borderRadius:4, background:"#fff7ed", border:"1px solid #fed7aa", color:"#92400e", fontFamily:"Arial, sans-serif", fontWeight:600 }}>🟡 Noted \u2014 standard for client type, on file</span>
              <span style={{ fontSize:11, padding:"3px 10px", borderRadius:4, background:"#eff6ff", border:"1px solid #bfdbfe", color:"#1e40af", fontFamily:"Arial, sans-serif", fontWeight:600 }}>ℹ️ Informational \u2014 context only</span>
            </div>
            {report.riskFactors.map((f, i) => {
              const factor = typeof f === "string" ? f : f.factor;
              const notes = typeof f === "object" ? f.notes : null;
              const rating = typeof f === "object" ? f.rating : null;
              const ratingColor = rating === "High" ? "#dc2626" : rating === "Medium" ? "#d97706" : "#15803d";
              const bg = rating === "High" ? "#fff1f2" : rating === "Medium" ? "#fff7ed" : "#f0fdf4";
              const border = rating === "High" ? "#fecaca" : rating === "Medium" ? "#fed7aa" : "#bbf7d0";
              return (
                <div key={i} style={{ marginBottom:8, padding:"12px 14px", background:bg, borderRadius:6, border:`1px solid ${border}` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, marginBottom: notes ? 6 : 0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"#111827", fontFamily:"Arial, sans-serif" }}>{factor}</div>
                    {rating && <span style={{ fontSize:10, fontWeight:700, color:ratingColor, whiteSpace:"nowrap", padding:"2px 8px", borderRadius:4, background:`${ratingColor}18`, border:`1px solid ${ratingColor}40`, flexShrink:0 }}>{rating}</span>}
                  </div>
                  {notes && <div style={{ fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{notes}</div>}
                </div>
              );
            })}
          </div>
        )}

        {/* Travel Rule Detail */}
        {report.travelRule?.wallets?.length > 0 && (
          <div style={S.section}>
            <div style={S.sectionTitle}>8. Travel Rule \u2014 Wallet Ownership Verification</div>
            <div style={{ ...S.para, color:"#6b7280", marginBottom:14 }}>
              The following table documents Travel Rule compliance for each virtual asset wallet associated with this client, as required under applicable AML regulations and FATF guidance on virtual assets.
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse", border:"1px solid #e5e7eb", borderRadius:8, overflow:"hidden" }}>
              <thead>
                <tr style={{ background:"#1e2d5a" }}>
                  {["Wallet Address","Type","Exchange / Method","Status"].map(h => (
                    <th key={h} style={{ padding:"8px 10px", textAlign:"left", fontSize:11, color:"white", fontFamily:"Arial, sans-serif", fontWeight:700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.travelRule.wallets.map((w, i) => (
                  <tr key={i} style={{ background: i%2===0 ? "#fff" : "#f9fafb", borderBottom:"1px solid #f3f4f6" }}>
                    <td style={{ padding:"8px 10px", fontSize:11, color:"#374151", fontFamily:"monospace" }}>{w.address?.slice(0,16)}...{w.address?.slice(-6)}</td>
                    <td style={{ padding:"8px 10px", fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif", textTransform:"capitalize" }}>{w.type || "\u2014"}</td>
                    <td style={{ padding:"8px 10px", fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif" }}>
                      {w.type === "exchange" ? `${w.exchangeName || "\u2014"} ${w.exchangeRegulated ? "· Regulated" : "· Unregulated"} ${w.accountInEntityName ? "· Account verified" : ""}` :
                       w.type === "private" ? `Proof of control: ${w.proofOfControl?.method === "satoshi_test_complete" ? `Satoshi test verified ${w.proofOfControl.verifiedDate ? new Date(w.proofOfControl.verifiedDate).toLocaleDateString("en-GB") : ""}` : "Pending"}` : "\u2014"}
                    </td>
                    <td style={{ padding:"8px 10px" }}>
                      <span style={S.badge(w.travelRuleStatus === "compliant" ? "#15803d" : "#d97706")}>
                        {w.travelRuleStatus === "compliant" ? "✓ Compliant" : w.travelRuleStatus === "pending_satoshi_test" ? "Satoshi pending" : w.travelRuleStatus === "pending_client_response" ? "Awaiting client" : "Pending"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* v1_847: the "Ask a Question About This File" panel is removed. It called
            /api/command-interface with no credential, and that route admits only a
            signed in member of the case's firm, so for the external reviewer every question
            came back "Error: Not signed in". A control that can never work is
            hidden, not left to fail. */}

        {/* Decision Panel */}
        <div style={{ ...S.section, border: "2px solid #1e2d5a" }}>
          <div style={S.sectionTitle}>Jaybee Compliance \u2014 Decision</div>
          <div style={{ ...S.para, marginBottom: 16 }}>
            Having reviewed the above client file prepared by {firmObject}'s Compliance Manager, please record your decision below. Your decision will be immediately logged and {firmObject} notified.
          </div>

          <div style={{ padding:"12px 16px", background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:8, marginBottom:20 }}>
            <div style={{ fontSize:12, color:"#0369a1", fontFamily:"Arial, sans-serif", lineHeight:1.7 }}>
              <strong>Please note:</strong> Recording your approval here does <strong>not</strong> automatically send a welcome email to the client. Your approval is confirmation of your sign off on this file. {firmSubject} will send the client their welcome email manually once all final checks have been completed, including receipt of all signed compliance forms.
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginBottom: 6 }}>Your name *</label>
            <input value={decidedBy} onChange={e => setDecidedBy(e.target.value)} placeholder="e.g. Jean-Pierre Dubois, Jaybee Compliance" style={{ width: "100%", padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, fontFamily: "Arial, sans-serif", outline: "none" }} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginBottom: 6 }}>Notes / Conditions (required if requesting information or declining)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any conditions, comments, or reasons for your decision..." style={{ width: "100%", padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, fontFamily: "Arial, sans-serif", resize: "vertical", minHeight: 80, outline: "none" }} />
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={() => submitDecision("approve")} disabled={submitting} style={{ ...S.btn("#15803d", "#fff"), opacity: submitting ? 0.6 : 1 }}>
              {submitting ? "Submitting..." : "✓ Approve"}
            </button>
            <button onClick={() => submitDecision("info")} disabled={submitting} style={{ ...S.btn("#d97706", "#fff"), opacity: submitting ? 0.6 : 1 }}>
              Request More Information
            </button>
            <button onClick={() => submitDecision("decline")} disabled={submitting} style={{ ...S.btn("#dc2626", "#fff"), opacity: submitting ? 0.6 : 1 }}>
              Decline
            </button>
          </div>
        </div>

        <div style={{ textAlign: "center", padding: "16px", fontFamily: "Arial, sans-serif", fontSize: 11, color: "#888888" }}>
          Confidential, prepared for Jaybee Compliance by {firmObject} · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
